import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExport,
  faCode,
  faCube,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";
import CommonButton from "../../elments/CommonButton";
import "./styles/ExportOptionsDropdown.css";

const ExportOptionsDropdown = ({
  features = {},
  projectId,
  sdataGroup,
  setSdataGroup,
  banners,
  selectedProject,
  selectedClient,
  surveyName,
  surveyFileName,
  user,
  powner,
  list,
  columnIndex,
  autoPreQuotes,
  alphaOrder,
  hideme,
  qtitlehide,
  removebold,
  removinstt,
  quotaData,
  templateOptions = [],
  selectedTemplateValue,
  onExportFilesUpdate,
}) => {
  const { toast } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [exportingType, setExportingType] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);

  const getSelectedTemplate = () => {
    return (
      templateOptions.find((t) => t.value === selectedTemplateValue) ||
      templateOptions[0]
    );
  };

  const trackDownload = async (exportType, success = true, errorMessage = null) => {
    try {
      await fetch(`${apiService.baseURL}/track-download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userEmail: user?.user?.email || user?.email || "unknown@email.com",
          projectId,
          projectName: selectedProject?.name || "Unknown Project",
          clientName: selectedClient?.name || "Unknown Client",
          surveyName: surveyName || surveyFileName || "Untitled Survey",
          exportType,
          timestamp: new Date().toISOString(),
          success,
          errorMessage,
        }),
      });
    } catch (error) {
      console.error("Error tracking download:", error);
    }
  };

  const handleExport = async (exportType, additionalOptions = {}) => {
    setIsExporting(true);
    setExportingType(exportType);

    setSdataGroup((prevDataGroup) => {
      const updatedDataGroup = { ...prevDataGroup };

      const requestBody = {
        projectId,
        dataList: updatedDataGroup,
        banners,
        project: JSON.stringify(selectedProject),
        client: JSON.stringify(selectedClient),
        list,
        type: exportType,
        columnIndex,
        autoPreQuotes,
        alphaOrder,
        hideme,
        qtitlehide,
        removebold,
        removinstt,
        user: powner,
        quotaData,
        ...additionalOptions,
      };

      if (exportType === "confirmit") {
        const selectedTemplate = getSelectedTemplate();
        requestBody.templateName = selectedTemplate.value;
        requestBody.insertionPoint = selectedTemplate.columns;
        requestBody.listPoint = selectedTemplate.lstcol;
      }

      fetch(`${apiService.baseURL}/generate-export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(requestBody),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/zip")) {
            return response.blob().then((blob) => ({ blob, isZip: true }));
          } else if (contentType && contentType.includes("application/json")) {
            return response.json();
          } else {
            return response.text();
          }
        })
        .then((data) => {
          trackDownload(exportType, true, null);

          if (data && data.isZip) {
            const link = document.createElement("a");
            link.href = URL.createObjectURL(data.blob);
            link.download = `${surveyName || surveyFileName || "survey"}_${exportType}.zip`;
            link.click();
            toast.success("Confirmit zip file saved successfully!");
            return;
          } else if (exportType === "decipher") {
            const blob = new Blob(
              [typeof data === "string" ? data : JSON.stringify(data)],
              { type: "text/xml" }
            );
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${surveyName || surveyFileName || "survey"}_decipher.xml`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success("Decipher XML file saved successfully!");
            return;
          } else if (exportType === "dimension") {
            const blob = new Blob(
              [typeof data === "string" ? data : JSON.stringify(data)],
              { type: "text/xml" }
            );
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${surveyName || surveyFileName || "survey"}_dimension.mdd`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success("Dimension MDD file saved successfully!");
            return;
          } else if (exportType === "qualtrics") {
            const blob = new Blob(
              [typeof data === "string" ? data : JSON.stringify(data, null, 2)],
              { type: "application/json" }
            );
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${surveyName || surveyFileName || "survey"}_qualtrics.qsf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            toast.success("Qualtrics QSF file saved successfully!");
            return;
          }

          const fileExtensions = {
            sawtooth: "SSI",
            pollstudio: "txt",
            confirmit: "xml",
            decipher: "xml",
            qualtrics: "qsf",
            dimension: "mdd",
            dimensionRoute: "txt",
            intellisurvey: "txt",
          };

          const successMessages = {
            sawtooth: "Sawtooth file saved successfully!",
            pollstudio: "Poll Studio file saved successfully!",
            confirmit: "Confirmit file saved successfully!",
            decipher: "Decipher file saved successfully!",
            qualtrics: "Qualtrics file saved successfully!",
            dimension: "Dimension file saved successfully!",
            dimensionRoute: "Dimension routes file saved successfully!",
            intellisurvey: "Intelli Survey file saved successfully!",
          };

          const exportData = {
            content: data,
            type: exportType,
            extension: fileExtensions[exportType] || "txt",
            timestamp: new Date().toISOString(),
            fileName: `${surveyName || surveyFileName || "survey"}_${exportType}.${fileExtensions[exportType] || "txt"}`,
          };

          onExportFilesUpdate?.(exportType, exportData);
          toast.success(successMessages[exportType] || "File exported successfully!");
        })
        .catch((error) => {
          trackDownload(exportType, false, error.message);
          toast.error(`Failed to export ${exportType}. Please try again.`);
        })
        .finally(() => {
          setIsExporting(false);
          setExportingType("");
        });

      return updatedDataGroup;
    });
  };

  const createDecipherSurvey = async () => {
    setSdataGroup((prevDataGroup) => {
      const updatedDataGroup = { ...prevDataGroup };

      const makeDecipherRequests = async () => {
        try {
          const baseSurveyName =
            surveyName || surveyFileName?.replace(/\.[^/.]+$/, "") || "My Test Survey";
          const versionNumber = String(Math.floor(Math.random() * 900) + 100);
          const actualSurveyName = `${baseSurveyName} v${versionNumber}`;
          const surveyPath =
            baseSurveyName.toLowerCase().replace(/[^a-zA-Z0-9_]/g, "_") +
            "_v" +
            versionNumber +
            "_" +
            Date.now();

          const createResponse = await fetch(`${apiService.baseURL}/decipher/create-survey`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: actualSurveyName,
              ptype: "beacon",
              category: "internet",
              description: `Survey created from ${baseSurveyName} (Version ${versionNumber})`,
              prefPath: surveyPath,
            }),
          });

          if (!createResponse.ok) {
            const errorData = await createResponse.text();
            throw new Error(`Create survey failed: ${createResponse.status} - ${errorData}`);
          }

          const createResult = await createResponse.json();
          toast.success("Successfully saved the file");

          const generateResponse = await fetch(`${apiService.baseURL}/generate-titles`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              projectId,
              dataList: updatedDataGroup,
              banners,
              project: JSON.stringify(selectedProject),
              client: JSON.stringify(selectedClient),
              columnIndex,
            }),
          });

          if (!generateResponse.ok) {
            const errorData = await generateResponse.text();
            throw new Error(`Generate titles failed: ${generateResponse.status} - ${errorData}`);
          }

          const generatedData = await generateResponse.text();

          const uploadResponse = await fetch(`${apiService.baseURL}/decipher/upload-survey`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ surveyPath, xmlData: generatedData }),
          });

          if (!uploadResponse.ok) {
            const errorData = await uploadResponse.text();
            throw new Error(`Upload to Decipher failed: ${uploadResponse.status} - ${errorData}`);
          }

          const uploadResult = await uploadResponse.json();
          return { createResult, generatedData, uploadResult, surveyName: actualSurveyName, surveyPath, versionNumber };
        } catch (error) {
          console.error("Decipher API Error:", error);
          throw error;
        }
      };

      makeDecipherRequests()
        .then((results) => {
          trackDownload("decipher", true, null);
          toast.success(`Your survey "${results.surveyName}" successfully saved to Decipher!`);
        })
        .catch((error) => {
          trackDownload("decipher", false, error.message);
          let errorMessage = "Failed to save survey to Decipher";
          if (error.message) {
            if (error.message.includes("already exists")) {
              errorMessage = "Survey name already exists in Decipher. Please try again.";
            } else if (error.message.includes("400")) {
              errorMessage = "Invalid survey data. Please check your configuration.";
            } else if (error.message.includes("401") || error.message.includes("403")) {
              errorMessage = "Authentication error. Please check your Decipher credentials.";
            } else {
              errorMessage = `Error: ${error.message}`;
            }
          }
          toast.error(errorMessage);
        });

      return updatedDataGroup;
    });
  };

  // Single source of truth for every export option: whether it's enabled,
  // its icon/label/title, and its click handler. Used both to render the
  // dropdown grid AND to detect the "only one enabled" case so we can skip
  // the dropdown wrapper entirely and show that one button directly.
  const exportOptions = [
    {
      key: "sawtooth",
      enabled: !!features.sawtooth,
      icon: faFileExport,
      label: "ST",
      title: "Sawtooth",
      tooltip: "Export Sawtooth SSI file",
      onClick: () => handleExport("sawtooth"),
    },
    {
      key: "pollstudio",
      enabled: !!features.pollstudio,
      icon: faFileExport,
      label: "PS",
      title: "Poll Studio",
      tooltip: "Export Poll Studio JSON",
      onClick: () => handleExport("pollstudio"),
    },
    {
      key: "confirmit",
      enabled: !!features.confirmit,
      icon: faFileExport,
      label: "CF",
      title: "Confirmit",
      tooltip: "Export Confirmit XML",
      onClick: () => handleExport("confirmit"),
    },
    {
      key: "decipher_xml",
      enabled: !!features.decipher_xml,
      icon: faCode,
      label: "DCXML",
      title: "Decipher XML",
      tooltip: "Export Decipher XML",
      onClick: () => handleExport("decipher"),
    },
    {
      key: "decipher",
      enabled: !!features.decipher,
      icon: faCube,
      label: "DC",
      title: "Decipher",
      tooltip: "Create Decipher survey directly",
      onClick: () => createDecipherSurvey(),
    },
    {
      key: "dimension",
      enabled: !!features.dimension,
      icon: faFileExport,
      label: "DM",
      title: "Dimensions",
      tooltip: "Export Dimensions MDD file",
      onClick: () => handleExport("dimension"),
    },
    {
      key: "qualtrics",
      enabled: !!features.qualtrics,
      icon: faFileExport,
      label: "QT",
      title: "Qualtrics",
      tooltip: "Export Qualtrics QSF file",
      onClick: () => handleExport("qualtrics"),
    },
    {
      key: "intelli",
      enabled: !!features.intelli,
      icon: faFileExport,
      label: "IS",
      title: "Intelli Survey",
      tooltip: "Export Intelli Survey file",
      onClick: () => handleExport("intelli"),
    },
  ];

  const enabledExportOptions = exportOptions.filter((opt) => opt.enabled);

  return (
    <>
      {enabledExportOptions.length === 1 ? (
        // Only one export type is enabled — skip the modal entirely and
        // render that single option as a plain toolbar button, same
        // component/sizing as every other toolbar button (Global Settings,
        // Quota, Redirects, Actions, History, Preview, ...).
        <CommonButton
          symbol={enabledExportOptions[0].icon}
          label={enabledExportOptions[0].title}
          title={enabledExportOptions[0].tooltip}
          onClick={enabledExportOptions[0].onClick}
        />
      ) : enabledExportOptions.length > 1 ? (
        <>
          <CommonButton
            symbol={faFileExport}
            label="Export"
            title="Export options"
            onClick={() => setShowExportModal(true)}
          />

          {showExportModal && (
            <div className="export-modal-backdrop" onClick={() => setShowExportModal(false)}>
              <div className="export-modal-panel" onClick={(e) => e.stopPropagation()}>
                <div className="export-modal-header">
                  <h3 className="export-modal-title">
                    <FontAwesomeIcon icon={faFileExport} style={{ marginRight: "8px" }} />
                    Export Survey As
                  </h3>
                  <button
                    className="export-modal-close-btn"
                    onClick={() => setShowExportModal(false)}
                    title="Close"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <div className="export-modal-body">
                  <div className="export-modal-grid">
                    {enabledExportOptions.map((opt) => (
                      <button
                        key={opt.key}
                        className="export-modal-option-btn"
                        onClick={() => {
                          opt.onClick();
                          setShowExportModal(false);
                        }}
                        title={opt.tooltip}
                      >
                        <FontAwesomeIcon icon={opt.icon} /> <span>{opt.label}</span>
                        <small>{opt.title}</small>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : null}

      {isExporting && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
        >
          <div className="export-loader" />
          <p
            style={{
              color: "white",
              marginTop: "20px",
              fontSize: "18px",
              fontWeight: "600",
              textTransform: "capitalize",
            }}
          >
            Exporting {exportingType}...
          </p>
        </div>
      )}
    </>
  );
};

export default ExportOptionsDropdown;